import { unstableSetRender } from 'antd';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';

/**
 * Dạy antd cách dựng thành phần trên React 19.
 *
 * <b>ĐÂY LÀ MỘT LỖI ĐÃ CHẠY TRÊN MÁY CHỦ THẬT.</b> Người dùng bấm nút <i>Đặt lại mật khẩu</i> và
 * không thấy gì xảy ra. Nguyên nhân không nằm ở nút đó: mọi thành phần antd được gọi bằng
 * <b>hàm tĩnh</b> — <c>Modal.confirm</c> và toàn bộ hơn một trăm rưỡi lời gọi thông báo — đều im
 * lặng như nhau.
 *
 * <b>Vì sao hỏng.</b> Những hàm tĩnh ấy không nằm trong cây thành phần của ứng dụng, nên antd phải
 * tự dựng một cây React thứ hai vào một thẻ rời. Thư viện nội bộ của antd lấy hai hàm dựng cây từ
 * gói <c>react-dom</c>, mà React 19 đã bỏ cả hai:
 *
 * <list type="bullet">
 *   <item><c>ReactDOM.render</c> — bị gỡ hẳn ở React 19.</item>
 *   <item><c>ReactDOM.createRoot</c> — chuyển sang <c>react-dom/client</c>.</item>
 * </list>
 *
 * Nhận về hai giá trị rỗng, antd gọi vào chỗ trống và ném lỗi. Người dùng chỉ thấy: không có gì
 * xảy ra.
 *
 * <b>Cách sửa.</b> antd mở sẵn một lối khai báo chính thức để nơi dùng tự cung cấp cách dựng cây.
 * Tệp này khai đúng cách dựng của React 19, lấy từ <c>react-dom/client</c>.
 *
 * <b>Vì sao không cài gói vá của Ant Design.</b> Gói đó làm đúng chừng này việc. Mười lăm dòng đọc
 * hiểu được, nằm trong kho mã và giải thích được vì sao tồn tại, tốt hơn một phụ thuộc nữa phải
 * theo dõi lỗ hổng bảo mật.
 *
 * <b>Khi nào xóa tệp này.</b> Khi lên antd phiên bản 6 — bản đó dựng cho React 19 từ đầu.
 */

/** Thẻ chứa một cây React do antd dựng, có gắn sẵn gốc cây để dùng lại. */
interface ContainerWithRoot extends Element {
  /** Gốc cây React đã dựng cho thẻ này, nếu có. */
  _mvaErpRoot?: Root;
}

/**
 * Khai cách dựng cây cho antd. Gọi <b>một lần</b> lúc khởi động, trước khi dựng giao diện.
 */
export function setUpAntdForReact19(): void {
  /*
    antd đánh dấu lối khai này là "chưa ổn định" nên bộ lint chặn. Vẫn dùng vì đây là lối CHÍNH
    THỨC duy nhất antd mở ra để nơi dùng tự cung cấp cách dựng cây — gói vá do chính Ant Design
    phát hành cũng gọi đúng hàm này. Tắt đúng một dòng, không tắt cả quy tắc.
  */
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  unstableSetRender((node, container) => {
    const target = container as ContainerWithRoot;

    /*
      Dùng lại gốc cây đã dựng cho cùng một thẻ. Gọi createRoot hai lần trên một thẻ sẽ làm React
      cảnh báo và cây thứ hai ghi đè cây thứ nhất — hộp thoại thứ hai sẽ xóa mất hộp thoại đang mở.
    */
    target._mvaErpRoot ??= createRoot(target);

    const root = target._mvaErpRoot;

    root.render(node);

    return async () => {
      /*
        Nhường một nhịp trước khi gỡ cây. Gỡ ngay trong lúc React còn đang dựng sẽ ném lỗi
        "không được cập nhật khi đang vẽ", và antd gọi hàm này ngay giữa một vòng vẽ.
      */
      await Promise.resolve();

      root.unmount();
      delete target._mvaErpRoot;
    };
  });
}
